import { isApiError, reportClientError, type ClientErrorReport } from '@instanexus/sdk';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

/** How long a list stays fresh before a background refetch. */
const STALE_TIME_MS = 30_000;

/** Retries for a genuinely transient failure. Beyond this it is not transient. */
const MAX_RETRIES = 2;

/**
 * The permission a query or mutation is gated on, when its hook declared one as
 * `meta: { permission: '...' }`. The API's 403 body is deliberately generic (guardrail S8), so
 * this is how the console line can still name WHICH permission was missing.
 */
function permissionOf(meta: Readonly<Record<string, unknown>> | undefined): string | undefined {
  const permission = meta?.['permission'];
  return typeof permission === 'string' ? permission : undefined;
}

function report(
  source: Extract<ClientErrorReport['source'], 'query' | 'mutation'>,
  error: unknown,
  meta: Readonly<Record<string, unknown>> | undefined,
): void {
  reportClientError({
    source,
    message: source === 'query' ? 'Query failed' : 'Mutation failed',
    requestId: isApiError(error) ? error.requestId : undefined,
    cause: error,
    permission: permissionOf(meta),
  });
}

/**
 * TanStack Query owns every piece of server state in this app. A factory rather
 * than a module singleton so a test can build an isolated cache per render.
 *
 * Nothing 4xx is ever retried. A 401 with an expired token will be 401 again on
 * the next attempt — retrying it just delays the redirect to `/login` by three
 * round trips and, with the session-loss handler in `AuthProvider`, is exactly
 * how a sign-out turns into a retry loop. Server faults (5xx) still get a couple
 * of attempts, because those really can be transient.
 *
 * Every failed query and mutation goes through the shared `reportClientError` seam exactly
 * here, once, globally (ADR 0034 amendment point 3) — a permission denial becomes one clear
 * `console.warn` naming what was refused; nothing else reports. `AuthProvider`'s own cache
 * subscriptions react to 401/402 and deliberately do not report.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        report('query', error, query.meta);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        report('mutation', error, mutation.meta);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: STALE_TIME_MS,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          if (isApiError(error) && error.statusCode < 500) return false;
          return failureCount < MAX_RETRIES;
        },
      },
    },
  });
}
